import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Camera, Loader2, Package, Plus, Search } from "lucide-react";
import { toast } from "sonner";
import { AssetFormDialog } from "@/components/assets/AssetFormDialog";
import { AssetThumb } from "@/components/assets/AssetThumb";
import { InventorySummary } from "@/components/assets/InventorySummary";
import { CoinMascot } from "@/components/brand/CoinMascot";
import { EmptyState } from "@/components/common/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useHousehold } from "@/context/HouseholdContext";
import { CATEGORY_LABELS, useAssets } from "@/hooks/use-assets";
import { api, formatApiError } from "@/lib/api";
import { useFormat } from "@/lib/format";
import { cn } from "@/lib/utils";

const TONES = ["sky", "peach", "mint", "butter", "rose", "lilac"];

const PhotoButton = ({ onResult }) => {
  const { active } = useHousehold();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/households/${active.id}/assets/photo`, fd);
      if (!data.suggestion) toast.info("Photo enregistrée. L'analyse automatique n'a pas abouti : complétez la fiche.");
      onResult({ photo_id: data.photo_id, ...(data.suggestion || {}) });
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); if (ref.current) ref.current.value = ""; }
  };
  return (
    <>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" capture="environment" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="asset-photo-input" />
      <Button className="rounded-full" onClick={() => ref.current?.click()} disabled={busy} data-testid="asset-photo-button">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Camera className="mr-2 h-4 w-4" />}{busy ? "Analyse de la photo…" : "Ajouter en photo"}
      </Button>
    </>
  );
};

const WCHIP = { expired: ["Garantie expirée", "tone-rose"], soon: ["Garantie bientôt finie", "tone-butter"] };

const AssetCard = ({ a, tone }) => {
  const { money } = useFormat();
  const w = WCHIP[a.warranty_status];
  return (
    <Link to={`/foyer/equipements/${a.id}`} className={cn("card-surface tone-wash group flex items-center gap-3 p-3 transition-transform hover:-translate-y-0.5", `tone-${tone}`)} data-testid={`asset-card-${a.id}`}>
      <AssetThumb photoId={a.photo_id} icon={Package} className="h-14 w-14 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold">{a.name}</div>
        <div className="truncate text-xs text-muted-foreground">{[[a.brand, a.model !== "Autre" && a.model].filter(Boolean).join(" "), CATEGORY_LABELS[a.category]].filter(Boolean).join(" · ")}</div>
        {w && <span className={cn("tone-chip mt-1 inline-block rounded-full px-2 py-0.5 text-[10px] font-semibold", w[1])} data-testid={`asset-card-warranty-${a.id}`}>{w[0]}</span>}
      </div>
      {a.current_value != null && <span className="num shrink-0 text-right text-xs" data-testid={`asset-card-value-${a.id}`}><b>{money(a.current_value)}</b>{a.purchase_price != null && <span className="block text-[10px] text-muted-foreground line-through">{money(a.purchase_price)}</span>}</span>}
    </Link>
  );
};

export const EquipmentPanel = () => {
  const { data: assets, isLoading } = useAssets();
  const [dialog, setDialog] = useState(null);
  const [room, setRoom] = useState("all");
  const [q, setQ] = useState("");
  const groups = useMemo(() => {
    const list = (assets || []).filter((a) => (room === "all" || (a.room || "Sans pièce") === room) && `${a.name} ${a.brand || ""}`.toLowerCase().includes(q.toLowerCase()));
    return list.reduce((g, a) => ({ ...g, [a.room || "Sans pièce"]: [...(g[a.room || "Sans pièce"] || []), a] }), {});
  }, [assets, room, q]);
  const rooms = [...new Set((assets || []).map((a) => a.room || "Sans pièce"))];

  return (
    <section className="space-y-5" data-testid="equipment-panel">
      <div className="flex flex-wrap items-center gap-2">
        <PhotoButton onResult={(initial) => setDialog({ initial })} />
        <Button variant="outline" className="rounded-full" onClick={() => setDialog({ initial: {} })} data-testid="asset-add-manual-button"><Plus className="mr-2 h-4 w-4" />Saisie manuelle</Button>
        <div className="relative ml-auto w-full sm:w-56">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher" className="rounded-full pl-9" data-testid="asset-search-input" />
        </div>
      </div>
      <InventorySummary />
      {rooms.length > 1 && (
        <div className="flex flex-wrap gap-2" data-testid="asset-room-filters">
          {["all", ...rooms].map((r) => (
            <button key={r} onClick={() => setRoom(r)} data-testid={`asset-room-filter-${r}`}
              className={cn("rounded-full border px-3 py-1 text-xs font-medium transition-colors", room === r ? "border-transparent bg-navy text-[#F8FAF8] dark:bg-mint dark:text-navy" : "border-border bg-card hover:bg-secondary")}>
              {r === "all" ? "Toutes les pièces" : r}
            </button>
          ))}
        </div>
      )}
      {isLoading ? null : !assets?.length ? (
        <div className="card-surface flex flex-col items-center gap-3 p-10 text-center" data-testid="assets-empty">
          <CoinMascot size={64} />
          <div className="font-heading font-semibold">Votre inventaire est vide</div>
          <p className="max-w-xs text-sm text-muted-foreground">Prenez un équipement en photo : CASHMY propose son nom et la pièce où il se trouve.</p>
        </div>
      ) : !Object.keys(groups).length ? <EmptyState compact title="Aucun résultat" /> : Object.entries(groups).map(([r, list], gi) => (
        <div key={r} data-testid={`asset-group-${r}`}>
          <h3 className="mb-2 text-sm font-semibold">{r} <span className="text-muted-foreground">({list.length})</span></h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{list.map((a) => <AssetCard key={a.id} a={a} tone={TONES[gi % TONES.length]} />)}</div>
        </div>
      ))}
      {dialog && <AssetFormDialog open onOpenChange={(o) => !o && setDialog(null)} initial={dialog.initial} />}
    </section>
  );
};
